/**
 * Énumérations partagées — source de vérité : annexe A du guide
 * (`docs/guides/FlashPay-Guide-Integration-API-v1.2.pdf`).
 *
 * Les valeurs circulent en chaînes MAJUSCULES et sont transmises
 * littéralement : une valeur inconnue provoque une erreur de conversion
 * côté serveur, pas un défaut silencieux (§4.4).
 *
 * Attention à `FeePayer` : la liste admise diffère selon le service
 * (annexe A, « Énumérations définies dans plusieurs services ») — utiliser
 * `FEE_PAYERS_WALLET` côté wallet-service et `FEE_PAYERS_FEE_ENGINE` côté
 * fee-engine-service.
 */

/* ------------------------- Portefeuilles et transactions ------------------------ */

export const BENEFICIARY_TYPES = ["FLASH_WALLET", "BANK_ACCOUNT", "MOBILE_MONEY"] as const;
export type BeneficiaryType = (typeof BENEFICIARY_TYPES)[number];

export const DESTINATION_EXTERNAL_TYPES = ["NONE", "BANK", "MOBILE_MONEY"] as const;
export type DestinationExternalType = (typeof DESTINATION_EXTERNAL_TYPES)[number];

/** FeePayer tel qu'admis par wallet-service (sans SPLIT). */
export const FEE_PAYERS_WALLET = ["SENDER", "RECEIVER", "PLATFORM"] as const;
export type FeePayerWallet = (typeof FEE_PAYERS_WALLET)[number];

export const TRANSACTION_STATUSES = [
  "PENDING",
  "AWAITING_LEDGER",
  "COMPLETED",
  "DISPUTED",
  "FAILED",
  "REVERSED",
  "CANCELLED",
] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

export const TRANSACTION_TYPES = [
  "TRANSFER_INTERNAL",
  "TRANSFER_BANK",
  "TRANSFER_MOBILE_MONEY",
  "BILL_PAYMENT",
  "MERCHANT_QR_PAYMENT",
  "POT_CONTRIBUTION",
  "POT_WITHDRAWAL",
  "PAYROLL_CREDIT",
  "AGENT_CASH_IN",
  "AGENT_CASH_OUT",
  "EMONEY_ISSUANCE",
  "EMONEY_REDEMPTION",
  /** Guide v2.4 §6.3 — annulation d'un transfert Non-Member non encaissé. */
  "NONMEMBER_EXPIRY_REFUND",
  /** Guide v3.1 §6.10 — versement / retrait d'un coffre d'épargne (produits savings-service, visibles dans l'historique wallet). */
  "SAVINGS_DEPOSIT",
  "SAVINGS_WITHDRAWAL",
] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const WALLET_HOLD_STATUSES = ["ACTIVE", "CAPTURED", "RELEASED", "EXPIRED"] as const;
export type WalletHoldStatus = (typeof WALLET_HOLD_STATUSES)[number];

export const WALLET_KYC_LEVELS = ["TIER0", "TIER1", "TIER2"] as const;
export type WalletKycLevel = (typeof WALLET_KYC_LEVELS)[number];

export const WALLET_OWNER_TYPES = [
  "INDIVIDUAL",
  "MERCHANT",
  "COMPANY",
  "AGENT",
  "PLATFORM",
] as const;
export type WalletOwnerType = (typeof WALLET_OWNER_TYPES)[number];

export const WALLET_STATUSES = ["PENDING_KYC", "ACTIVE", "SUSPENDED", "CLOSED"] as const;
export type WalletStatus = (typeof WALLET_STATUSES)[number];

/**
 * Purpose d'un portefeuille (annexe A v3.1) : `MAIN` = portefeuille
 * principal (défaut serveur), `SAVINGS_VAULT` = portefeuille-coffre créé par
 * savings-service (jamais choisi implicitement ; visible dans `Wallets/all`
 * uniquement via le filtre `purpose=SAVINGS_VAULT`).
 */
export const WALLET_PURPOSES = ["MAIN", "SAVINGS_VAULT"] as const;
export type WalletPurpose = (typeof WALLET_PURPOSES)[number];

/* --------------------------------- KYC et identités ----------------------------- */

export const AGENT_STATUSES = ["ACTIVE", "INACTIVE", "SUSPENDED"] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

export const AGENT_TYPES = ["BOTH", "KYC_ENROLLMENT", "CASH_AGENT"] as const;
export type AgentType = (typeof AGENT_TYPES)[number];

export const KYC_CASE_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "IN_REVIEW",
  "APPROVED",
  "REJECTED",
  "EXPIRED",
] as const;
export type KycCaseStatus = (typeof KYC_CASE_STATUSES)[number];

export const KYC_CHANNELS = ["SELF_SERVICE", "AGENT_ASSISTED"] as const;
export type KycChannel = (typeof KYC_CHANNELS)[number];

export const KYC_DOCUMENT_STATUSES = ["PENDING", "VALIDATED", "REJECTED"] as const;
export type KycDocumentStatus = (typeof KYC_DOCUMENT_STATUSES)[number];

export const KYC_DOCUMENT_TYPES = [
  "NATIONAL_ID",
  "PASSPORT",
  "PROOF_OF_ADDRESS",
  "SELFIE",
  "BUSINESS_REGISTRATION",
] as const;
export type KycDocumentType = (typeof KYC_DOCUMENT_TYPES)[number];

export const KYC_STATUSES = ["NONE", "PENDING", "TIER1", "TIER2", "REJECTED"] as const;
export type KycStatus = (typeof KYC_STATUSES)[number];

export const PARTY_TYPES = ["INDIVIDUAL", "COMPANY"] as const;
export type PartyType = (typeof PARTY_TYPES)[number];

export const PREFERRED_LANGUAGES = ["FR", "EN", "ZH"] as const;
export type PreferredLanguage = (typeof PREFERRED_LANGUAGES)[number];

export const SANCTIONS_SCREENING_STATUSES = [
  "NOT_SCREENED",
  "CLEAR",
  "HIT",
  "UNDER_REVIEW",
] as const;
export type SanctionsScreeningStatus = (typeof SANCTIONS_SCREENING_STATUSES)[number];

export const UPLOADED_VIA = ["SELF", "AGENT"] as const;
export type UploadedVia = (typeof UPLOADED_VIA)[number];

/* ------------------------------------ Comptabilité ------------------------------ */

export const ACCOUNT_ROLES = [
  "CUSTOMER_WALLET",
  "MERCHANT_SETTLEMENT",
  "FEE_REVENUE",
  "PROVIDER_SETTLEMENT",
  "TAX_PAYABLE",
  "COMPANY_PAYROLL_ACCOUNT",
  "AGENT_COMMISSION_EXPENSE",
  "AGENT_COMMISSION_PAYABLE",
  "SUSPENSE",
  "TRUST_BANK",
] as const;
export type AccountRole = (typeof ACCOUNT_ROLES)[number];

export const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const AMOUNT_BASIS = ["PRINCIPAL", "FEE", "TAX", "NET", "AGENT_COMMISSION"] as const;
export type AmountBasis = (typeof AMOUNT_BASIS)[number];

export const JOURNAL_ENTRY_STATUSES = ["PENDING", "POSTED", "REJECTED", "REVERSED"] as const;
export type JournalEntryStatus = (typeof JOURNAL_ENTRY_STATUSES)[number];

export const POSTING_DIRECTIONS = ["DEBIT", "CREDIT"] as const;
export type PostingDirection = (typeof POSTING_DIRECTIONS)[number];

export const POSTING_TEMPLATE_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"] as const;
export type PostingTemplateStatus = (typeof POSTING_TEMPLATE_STATUSES)[number];

export const PRODUCT_CATEGORIES = [
  "WALLET",
  "PAYROLL",
  "MERCHANT",
  "POT",
  "BILL",
  "AGENT",
  "PLATFORM",
] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const PROVIDER_STATUSES = ["ACTIVE", "INACTIVE"] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export const PROVIDER_TYPES = ["BANK", "MOBILE_MONEY", "BILLER", "INTERNAL"] as const;
export type ProviderType = (typeof PROVIDER_TYPES)[number];

/* -------------------------------- Frais et tarification --------------------------- */

export const CUSTOMER_SEGMENTS = ["INDIVIDUAL", "MERCHANT", "COMPANY"] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

export const DISCOUNT_TYPES = ["PERCENT_OFF_FEE", "FIXED_OFF_FEE", "FEE_WAIVED"] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

export const FEE_CALCULATION_TYPES = ["PERCENTAGE", "FIXED", "TIERED"] as const;
export type FeeCalculationType = (typeof FEE_CALCULATION_TYPES)[number];

/** FeePayer tel qu'admis par fee-engine-service (avec SPLIT). */
export const FEE_PAYERS_FEE_ENGINE = ["SENDER", "RECEIVER", "PLATFORM", "SPLIT"] as const;
export type FeePayerFeeEngine = (typeof FEE_PAYERS_FEE_ENGINE)[number];

export const FEE_PURPOSES = [
  "CUSTOMER_FEE",
  "AGENT_COMMISSION",
  "PLATFORM_SUBSCRIPTION",
] as const;
export type FeePurpose = (typeof FEE_PURPOSES)[number];

export const FEE_RULE_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"] as const;
export type FeeRuleStatus = (typeof FEE_RULE_STATUSES)[number];

export const PROMOTION_STATUSES = ["DRAFT", "ACTIVE", "EXPIRED", "DISABLED"] as const;
export type PromotionStatus = (typeof PROMOTION_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = ["ACTIVE", "CANCELLED", "PAST_DUE"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const TIER_BASIS = ["TRANSACTION_AMOUNT", "PARTY_MONTHLY_VOLUME"] as const;
export type TierBasis = (typeof TIER_BASIS)[number];

/* -------------------------------------- Marchands -------------------------------- */

export const MERCHANT_KYC_STATUSES = ["NONE", "PENDING", "TIER1", "TIER2", "REJECTED"] as const;
export type MerchantKycStatus = (typeof MERCHANT_KYC_STATUSES)[number];

export const MERCHANT_PERMISSION_CODES = [
  "MANAGE_SETTLEMENT_CONFIG",
  "MANAGE_BANK_ACCOUNT",
  "CREATE_SUB_MERCHANT",
  "CREATE_SALES_AGENT",
  "REVOKE_QR_CODE",
  "VIEW_CONSOLIDATED_SALES",
  "CLOSE_ACCOUNT",
] as const;
export type MerchantPermissionCode = (typeof MERCHANT_PERMISSION_CODES)[number];

export const MERCHANT_STATUSES = ["PENDING", "ACTIVE", "SUSPENDED", "CLOSED"] as const;
export type MerchantStatus = (typeof MERCHANT_STATUSES)[number];

export const MERCHANT_TYPES = ["MASTER", "SUB_MERCHANT"] as const;
export type MerchantType = (typeof MERCHANT_TYPES)[number];

export const PAYOUT_METHODS = ["FLASH_WALLET", "BANK_ACCOUNT"] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export const QR_STATUSES = ["ACTIVE", "REVOKED", "EXPIRED"] as const;
export type QrStatus = (typeof QR_STATUSES)[number];

export const QR_TYPES = ["STATIC", "DYNAMIC"] as const;
export type QrType = (typeof QR_TYPES)[number];

export const SALE_STATUSES = ["PENDING", "SETTLED", "REVERSED"] as const;
export type SaleStatus = (typeof SALE_STATUSES)[number];

export const SALES_AGENT_STATUSES = ["ACTIVE", "INACTIVE", "SUSPENDED"] as const;
export type SalesAgentStatus = (typeof SALES_AGENT_STATUSES)[number];

export const SETTLEMENT_FREQUENCIES = ["INSTANT", "DAILY", "WEEKLY"] as const;
export type SettlementFrequency = (typeof SETTLEMENT_FREQUENCIES)[number];

export const STORE_STATUSES = ["ACTIVE", "INACTIVE"] as const;
export type StoreStatus = (typeof STORE_STATUSES)[number];

/* ------------------------------------ Notifications ------------------------------ */

export const NOTIFICATION_CHANNELS = ["SMS", "EMAIL", "PUSH"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_LANGUAGES = ["FR", "EN", "ZH"] as const;
export type NotificationLanguage = (typeof NOTIFICATION_LANGUAGES)[number];

export const NOTIFICATION_STATUSES = ["QUEUED", "SENT", "DELIVERED", "FAILED"] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

/** Plateforme d'un appareil PUSH (`NotificationDevices`, guide v1.4). */
export const DEVICE_PLATFORMS = ["WEB", "ANDROID", "IOS"] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

/* ------------------------------------ Administration ----------------------------- */

export const APPROVAL_ACTION_TYPES = [
  "FEE_RULE_CHANGE",
  "LEDGER_MANUAL_REVERSAL",
  "KYC_FORCE_OVERRIDE",
  "EMONEY_ISSUANCE",
  "EMONEY_REDEMPTION",
  /** Guide v3.1 §6.10 — déblocage anticipé d'un coffre d'épargne. */
  "SAVINGS_EARLY_UNLOCK",
] as const;
export type ApprovalActionType = (typeof APPROVAL_ACTION_TYPES)[number];

/**
 * Types de ressources visées par une demande d'approbation (`resourceType`).
 * Valeurs observées dans le code + annexe v3.1 (`SAVINGS_VAULT` pour les
 * coffres d'épargne) — le champ reste une chaîne libre côté DTO.
 */
export const APPROVAL_RESOURCE_TYPES = [
  "FeeRule",
  "JournalEntry",
  "Party",
  "Wallet",
  "SAVINGS_VAULT",
] as const;
export type ApprovalResourceType = (typeof APPROVAL_RESOURCE_TYPES)[number];

export const APPROVAL_STATUSES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
  "APPLIED",
  "FAILED",
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

/* -------------------------------------- Passerelle ------------------------------- */

export const API_CLIENT_STATUSES = ["ACTIVE", "REVOKED"] as const;
export type ApiClientStatus = (typeof API_CLIENT_STATUSES)[number];
